import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Trophy, TrendingUp, DollarSign, Building2, Loader2 } from 'lucide-react';
import { classificarIcp } from './conservacao-icp-regras';

// =====================================================================
// Dashboard / Histórico anual da Conservação Predial (seções 11 e 12 da spec).
//
// Lê GET /historico (recortado por loja no backend) e monta:
//   · ranking de ICP entre restaurantes (o último CERTIFICADO de cada loja)
//   · histórico anual por restaurante (ano · dias · itens · custo · ICP)
//   · totais da rede
//
// ⚠️ O "resultado do ano" de uma loja é o ÚLTIMO programa CERTIFICADO daquele
//    ano — não a média. Somar/mediar dois ICPs do mesmo ano seria média de
//    porcentagem sobre bases diferentes (regra registrada no PLANO). Programas
//    ainda em andamento aparecem no histórico, marcados, mas não contam para o
//    ranking.
// =====================================================================

const API = '/api/manutencao/conservacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');

// ⚠️ A COR SAI DA CLASSIFICAÇÃO, NUNCA DE CORTES PRÓPRIOS. Este mapa já foi
// uma segunda cópia dos cortes do ICP (95/90/80/70) e, quando a régua virou
// binária em 2026-09-09 e os cortes caíram para 90/80/65/50, ele passaria a
// pintar um ICP de 85 com a cor de "Bom" enquanto o rótulo ao lado dizia
// "Muito Bom" — divergência que ninguém liga à causa. Os cortes vivem em UM
// lugar: conservacao-icp-regras.js (espelho travado do motor do servidor).
const CORES_ICP = {
  Excelente: { bg: '#d1fae5', color: '#065f46' },
  'Muito Bom': { bg: '#dbeafe', color: '#1e40af' },
  Bom: { bg: '#e0e7ff', color: '#4338ca' },
  Regular: { bg: '#fef3c7', color: '#92400e' },
  'Necessita Intervenção': { bg: '#fee2e2', color: '#991b1b' },
};

const corIcp = (icp) => {
  if (icp == null) return { bg: '#f1f5f9', color: '#64748b' };
  return CORES_ICP[classificarIcp(icp)] || { bg: '#f1f5f9', color: '#64748b' };
};

const ConservacaoDashboard = ({ onVoltar }) => {
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}/historico`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLinhas(await r.json());
    } catch (e) {
      setErro('Erro ao carregar o histórico.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Agrupa por loja preservando a ordem que veio (loja, ano desc, data desc).
  const porLoja = new Map();
  for (const l of linhas) {
    if (!porLoja.has(l.LOJA)) porLoja.set(l.LOJA, []);
    porLoja.get(l.LOJA).push(l);
  }

  // Ranking: o último CERTIFICADO de cada loja (as linhas já vêm com o mais
  // recente primeiro dentro da loja, então o primeiro certificado é o último).
  const ranking = [];
  for (const [loja, progs] of porLoja) {
    const ultimoCert = progs.find((p) => p.STATUS === 'Certificado');
    if (ultimoCert) ranking.push({ loja, ...ultimoCert });
  }
  ranking.sort((a, b) => Number(b.ICP) - Number(a.ICP));

  const totalInvestido = linhas.reduce((s, l) => s + Number(l.CUSTO || 0), 0);
  const totalCertificados = linhas.filter((l) => l.STATUS === 'Certificado').length;

  if (carregando) {
    return <p style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={16} className="spin" /> Carregando…</p>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onVoltar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Histórico e indicadores</h3>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

      {linhas.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Ainda não há programas para consolidar.
        </div>
      ) : (
        <>
          {/* --- totais da rede --- */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            <div style={{ padding: 16, background: '#eef2ff', borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase' }}>
                <Building2 size={14} /> Restaurantes
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#312e81', fontVariantNumeric: 'tabular-nums' }}>{porLoja.size}</div>
            </div>
            <div style={{ padding: 16, background: '#d1fae5', borderRadius: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#065f46', textTransform: 'uppercase' }}>Certificados</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#065f46', fontVariantNumeric: 'tabular-nums' }}>{totalCertificados}</div>
            </div>
            <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>
                <DollarSign size={14} /> Investido
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{brl(totalInvestido)}</div>
            </div>
          </div>

          {/* --- ranking de ICP --- */}
          {ranking.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trophy size={17} color="#f59e0b" /> Ranking de ICP (último certificado por loja)
              </h4>
              <div style={{ display: 'grid', gap: 8 }}>
                {ranking.map((r, i) => {
                  const c = corIcp(Number(r.ICP));
                  return (
                    <div key={r.loja} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ width: 24, textAlign: 'center', fontWeight: 800, color: i < 3 ? '#f59e0b' : '#94a3b8' }}>{i + 1}º</span>
                      <span style={{ minWidth: 60, fontWeight: 800, color: '#0f172a' }}>{r.loja}</span>
                      <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${r.ICP}%`, height: '100%', background: c.color, borderRadius: 999 }} />
                      </div>
                      <span style={{ minWidth: 62, textAlign: 'right', fontWeight: 800, color: c.color, fontVariantNumeric: 'tabular-nums' }}>
                        {Number(r.ICP).toFixed(1)}%
                      </span>
                      <span style={{ minWidth: 90, textAlign: 'right', fontSize: 12, color: '#64748b' }}>{r.ANO}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* --- histórico anual por restaurante --- */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
            <h4 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={17} color="#4f46e5" /> Histórico anual por restaurante
            </h4>
            {[...porLoja.entries()].map(([loja, progs]) => (
              <div key={loja} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{loja}</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#64748b' }}>
                        <th style={thStyle}>Inspeção</th>
                        <th style={thStyle}>Ano</th>
                        <th style={thStyle}>Situação</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Dias</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Itens</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Custo</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>ICP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {progs.map((pr) => {
                        const c = corIcp(pr.ICP == null ? null : Number(pr.ICP));
                        return (
                          <tr key={pr.ID} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={tdStyle}>{dataBr(pr.DATA_INSPECAO)}</td>
                            <td style={tdStyle}>{pr.ANO}</td>
                            <td style={tdStyle}>
                              <span style={{
                                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                                background: pr.STATUS === 'Certificado' ? '#d1fae5' : '#f1f5f9',
                                color: pr.STATUS === 'Certificado' ? '#065f46' : '#475569',
                              }}>{pr.STATUS}</span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{pr.DIAS_TOTAIS ?? '—'}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{pr.QTD_ITENS}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{brl(pr.CUSTO)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <span style={{ fontWeight: 800, color: c.color, fontVariantNumeric: 'tabular-nums' }}>
                                {pr.ICP == null ? '—' : `${Number(pr.ICP).toFixed(1)}%`}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const thStyle = { padding: '6px 10px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', color: '#334155' };

export default ConservacaoDashboard;
